import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, ChevronDown } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const KEY = "pb-site-switch-steps";
const STEPS = (feed) => [
  ["compare", "Check the side-by-side comparison below", "Every product should say Matches. Anything else is listed with the difference, so you can fix it here or accept it."],
  ["copy", "Copy the new feed address", feed],
  ["list", "In your pbtravelangel.com website project, replace the list address", "Find https://extras.pbtravelangel.com/api/public/products (often saved as a setting such as EXTRAS_FEED_URL) and replace it with the new feed address."],
  ["one", "Replace the single-product address too", "If the site loads one product at a time, change …/api/public/products/{slug} to …/api/extras/public/products/{slug}. Slugs are the same, so existing links keep working."],
  ["redeploy", "Redeploy the website and spot-check it", "Open a few product pages: pictures, prices, the App and PDF + App prices, and the Free sample link."],
  ["fallback", "Keep Extras running for a week", "If anything looks wrong, point the site back at the Extras address while you fix it. After that you can retire Extras."],
];

const fmt = (v) => (v === true ? "yes" : v === false ? "no" : v ?? "—");

const Compare = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [all, setAll] = useState(false);
  const load = () => { setD(null); setErr(""); http.get("/extras/feed-compare").then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e))); };
  useEffect(load, []);
  if (err) return <div data-testid="switch-compare-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}. <button onClick={load} className="underline">Try again</button></div>;
  if (!d) return <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 size={14} className="animate-spin" /> Reading both feeds…</div>;
  const rows = all ? d.rows : d.rows.filter((r) => r.state !== "match");
  const LABEL = { match: "Matches", differs: "Differs", only_extras: "Only in Extras", only_studio: "Only in Kit Studio" };
  return (
    <div data-testid="switch-compare">
      <div className="flex flex-wrap gap-2 text-xs mb-3">
        <span className="px-2 py-1 rounded bg-[#F1ECE4]">Extras {d.extras_count} · Kit Studio {d.studio_count}</span>
        <span data-testid="switch-match" className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{d.match} match</span>
        {["differs", "only_extras", "only_studio"].map((k) => d[k] > 0 && <span key={k} data-testid={`switch-${k}`} className="px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-200">{d[k]} {LABEL[k].toLowerCase()}</span>)}
        <label className="ml-auto flex items-center gap-1.5"><input data-testid="switch-show-all" type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Show matching products too</label>
      </div>
      {!rows.length && <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={15} /> Both feeds send exactly the same products, prices and details.</div>}
      <div className="divide-y divide-[#EFE9E0]">
        {rows.map((r) => (
          <div key={r.slug} data-testid={`switch-row-${r.slug}`} className="py-2.5 text-sm">
            <div className="flex gap-3"><span className="flex-1">{r.name}</span><span className={`text-xs ${r.state === "match" ? "text-emerald-700" : "text-amber-800"}`}>{LABEL[r.state]}</span></div>
            {r.diffs.length > 0 && <table className="mt-1.5 w-full text-xs"><thead><tr className="text-stone-500 text-left"><th className="font-normal w-40">Field</th><th className="font-normal">Extras feed</th><th className="font-normal">Kit Studio feed</th></tr></thead>
              <tbody>{r.diffs.map((x) => <tr key={x.field}><td className="py-0.5">{x.field}</td><td>{String(fmt(x.extras))}</td><td className="font-medium">{String(fmt(x.studio))}</td></tr>)}</tbody></table>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const SiteSwitch = ({ feedUrl }) => {
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState(() => JSON.parse(localStorage.getItem(KEY) || "{}"));
  const tick = (k) => { const n = { ...done, [k]: !done[k] }; setDone(n); localStorage.setItem(KEY, JSON.stringify(n)); };
  const steps = STEPS(feedUrl);
  const preview = window.location.hostname.includes("preview.emergentagent");
  return (
    <div data-testid="site-switch" className="ex-card p-6 mb-8">
      <button data-testid="site-switch-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 text-left">
        <span className="ex-display text-2xl">Switch pbtravelangel.com to this feed</span>
        <span className="ex-mono text-xs text-stone-500">{steps.filter(([k]) => done[k]).length}/{steps.length} done</span>
        <ChevronDown size={18} className={`ml-auto text-stone-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <>
        {preview && <div data-testid="site-switch-preview-warning" className="mt-4 flex gap-2 text-sm bg-amber-50 border border-amber-200 rounded p-3 text-amber-900"><AlertTriangle size={15} className="shrink-0 mt-0.5" />You're on the preview address. Do the switch from your live Kit Studio (kits.pbtravelangel.com) so the site gets the live feed address, not this preview one.</div>}
        <ol className="mt-5 space-y-3">
          {steps.map(([k, t, d], i) => (
            <li key={k} className="flex gap-3">
              <input data-testid={`site-switch-step-${k}`} type="checkbox" checked={!!done[k]} onChange={() => tick(k)} className="mt-1 w-4 h-4 accent-[#8B6B4A]" />
              <div><div className={`text-sm font-medium ${done[k] ? "line-through text-stone-400" : ""}`}>{i + 1}. {t}</div><div className="text-xs text-stone-500 break-all">{d}</div></div>
            </li>
          ))}
        </ol>
        <div className="border-t border-[#E6DDCF] mt-5 pt-4"><div className="ex-eyebrow mb-3">Side-by-side check</div><Compare /></div>
      </>}
    </div>
  );
};
